import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { auth } from "../lib/firebase";
import { API_BASE_URL, authenticatedFetch, fetchWithTimeout } from "../lib/api";
import { readStored, removeStored, writeStored } from "../lib/safeStorage";
import { replaceUrl } from "../lib/pageRouting";
import { forgetUrlSecret, readUrlSecret } from "../lib/urlSecrets";
import { isTerminalInviteAnswer, JOIN_UNREACHABLE, joinRefusal } from "../lib/joinRefusal";
import { paysForPersonalSubscription } from "../lib/billingAccess";
import { isConsoleRole } from "../lib/tenantAccess";
import { UserInfo, SubscriptionStatus } from "../types";

/**
 * Forget the invitation: the saved copy, and the one public/index.html kept from the URL (ENG-3430).
 * ENG-3449: only once it is over — joined, turned down, or refused for good (lib/joinRefusal). An
 * invitation the server left pending stays saved, so a reload tries it again.
 */
function endJoin() {
  removeStored('pendingJoinToken');
  forgetUrlSecret('join');
}

interface InviteContext {
  tenantName: string;
  inviterEmail: string;
  /** ENG-3904: null when the inviter set no name, and absent from an older backend. */
  inviterName?: string | null;
  invitedEmail: string;
  invitedName: string;
  /** ENG-3248: the invitation is worded for the plan, family or organization. Absent from an older backend. */
  tenantType?: string;
}

export function useJoinInvitation(args: {
  userInfo: UserInfo | null;
  subscriptionStatus: SubscriptionStatus | null;
  loadingSubscription: boolean;
  authHook: {
    setEmail: (v: string) => void;
    isSignUp: boolean;
    toggleSignUp: () => void;
    setFirstName: (v: string) => void;
    setLastName: (v: string) => void;
  };
  fetchSubscriptionStatus: () => Promise<SubscriptionStatus | null>;
  fetchBusinessMembers: () => void;
  fetchBusinessCustomSites: () => void;
}) {
  const {
    userInfo,
    subscriptionStatus,
    loadingSubscription,
    authHook,
    fetchSubscriptionStatus,
    fetchBusinessMembers,
    fetchBusinessCustomSites,
  } = args;

  /**
   * ENG-2722: the caller builds this hook's argument object inline in `App.tsx`, so `authHook` and
   * the three fetchers are a new identity on every render of the whole app. Naming them as effect
   * dependencies would re-run the invite flow constantly; omitting them needed a suppression. Both
   * are avoided by reading them off a ref that is refreshed each render, the same idiom as
   * `onPolicyChangedRef` in useOrgSettings.
   *
   * It also fixes a latent staleness bug: these are all read AFTER an await, so the closure value
   * could already be out of date — `authHook.isSignUp` in particular gated a `toggleSignUp()`.
   */
  const callerRef = useRef({ authHook, fetchSubscriptionStatus, fetchBusinessMembers, fetchBusinessCustomSites });
  callerRef.current = { authHook, fetchSubscriptionStatus, fetchBusinessMembers, fetchBusinessCustomSites };

  const [joinToken, setJoinToken] = useState<string | null>(null);
  const [joinResult, setJoinResult] = useState<{ success: boolean; message: string } | null>(null);
  const [pendingJoinToken, setPendingJoinToken] = useState<string | null>(null);
  const [joinConfirmDialogOpen, setJoinConfirmDialogOpen] = useState(false);
  const [inviteContext, setInviteContext] = useState<InviteContext | null>(null);
  /**
   * ENG-3449: the account the server last refused this invitation for, without ending it. The accept
   * effect does not ask again for that account, since its answer would not change until someone acts:
   * the person signs in as the invited address (a different uid, so it is asked again at once), or
   * an administrator frees a seat and the person reloads (which starts this at null).
   */
  const refusedForUidRef = useRef<string | null>(null);

  // ── Fetch invite preview when join token is available ──
  useEffect(() => {
    if (!joinToken) return;
    const fetchPreview = async () => {
      try {
        const response = await fetchWithTimeout(`${API_BASE_URL}/tenant/invite-preview/${joinToken}`);
        if (response.ok) {
          const data = await response.json();
          setInviteContext(data);
          const { authHook: ah } = callerRef.current;
          if (data.invitedEmail) {
            ah.setEmail(data.invitedEmail.toLowerCase());
            if (!ah.isSignUp) ah.toggleSignUp();
          }
          if (data.invitedName) {
            const nameParts = data.invitedName.split(' ');
            ah.setFirstName(nameParts[0] || '');
            ah.setLastName(nameParts.slice(1).join(' ') || '');
          }
        } else {
          const data = await response.json().catch(() => ({}));
          // ENG-3449: a 5xx says nothing about the invitation, so it is kept, as on a network error.
          if (!isTerminalInviteAnswer(response.status, data)) return;
          setJoinResult({ success: false, message: data.error || 'This invitation is no longer valid.' });
          setJoinToken(null);
          endJoin();
          replaceUrl(window.location.pathname);
        }
      } catch (e) {
        // Network error, fall back to regular signup
      }
    };
    fetchPreview();
  }, [joinToken]);

  // ENG-3904: the account whose accept is in flight. The effect re-runs while the POST is out, since the token
  // clears only on its answer; keyed by account so signing in as the invited address mid-flight still asks.
  const acceptingForUidRef = useRef<string | null>(null);

  /**
   * ENG-3449: an invitation the server left pending stays set, but is not asked again for this account.
   * Only the request is guarded: what follows an answer must not be reported as no answer.
   */
  const accept = useCallback(async (token: string, uid: string) => {
    if (acceptingForUidRef.current === uid) return;
    acceptingForUidRef.current = uid;
    // An answer for an account that has since signed out says nothing about the one signed in now.
    const signedOut = () => auth.currentUser?.uid !== uid;
    const keepPending = () => {
      refusedForUidRef.current = uid;
      setJoinToken(token);
    };
    try {
      let response: Response;
      try {
        response = await authenticatedFetch('/tenant/accept-invite', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
      } catch {
        if (signedOut()) return;
        setJoinResult({ success: false, message: JOIN_UNREACHABLE });
        keepPending();
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (signedOut()) return;
      if (response.ok) {
        setJoinResult({ success: true, message: data.message || `You've joined ${data.tenantName}!` });
        setJoinToken(null);
        endJoin();
        replaceUrl(window.location.pathname);
        const caller = callerRef.current;
        const subData = await caller.fetchSubscriptionStatus();
        if (subData?.role) {
          if (isConsoleRole(subData.role)) caller.fetchBusinessMembers();
          // ENG-3897: not for a family, which has no organization custom sites (ENG-3894: the backend
          // refuses the read), as App's own load skips it. Its people keep their own list, which the
          // sign-in load already read for someone in no organization.
          if (subData.tenantType !== 'family') caller.fetchBusinessCustomSites();
        }
        return;
      }
      const refusal = joinRefusal(response.status, data);
      setJoinResult({ success: false, message: refusal.message });
      if (refusal.terminal) {
        setJoinToken(null);
        endJoin();
      } else {
        keepPending();
      }
    } finally {
      if (acceptingForUidRef.current === uid) acceptingForUidRef.current = null;
    }
  }, []);

  // ── Accept join invitation ──
  useEffect(() => {
    if (!joinToken || !userInfo || !auth.currentUser) return;
    if (subscriptionStatus === null || loadingSubscription) return;
    const uid = auth.currentUser.uid;
    if (refusedForUidRef.current === uid || acceptingForUidRef.current === uid) return;

    // ENG-3725: only when accepting cancels something. `plan` is "premium" for every account.
    if (paysForPersonalSubscription(subscriptionStatus)) {
      setPendingJoinToken(joinToken);
      setJoinToken(null);
      setJoinConfirmDialogOpen(true);
      return;
    }
    accept(joinToken, uid);
  }, [joinToken, userInfo, subscriptionStatus, loadingSubscription, accept]);

  const handleConfirmJoinBusiness = useCallback(async () => {
    if (!pendingJoinToken || !auth.currentUser) return;
    setJoinConfirmDialogOpen(false);
    setPendingJoinToken(null);
    // A kept invitation goes back to the accept effect, so the dialog comes back on the next visit.
    await accept(pendingJoinToken, auth.currentUser.uid);
  }, [pendingJoinToken, accept]);

  const handleCancelJoinBusiness = useCallback(() => {
    setJoinConfirmDialogOpen(false);
    setPendingJoinToken(null);
    endJoin();
    replaceUrl(window.location.pathname);
  }, []);

  // ── Init helper: parse join token from URL or restore from localStorage ──
  // ENG-3411: through lib/safeStorage, like every other read and write of the saved token here. This
  // runs in App's init effect, ahead of the auth listener, so an unguarded read where site data is
  // blocked left the whole console on "Something went wrong". Without storage, a ?join link still
  // works for this visit; it just is not remembered past it.
  const initJoinToken = useCallback((updateIsPostInstall: (val: boolean) => void) => {
    // ENG-3430: the invitation token is no longer in the URL; public/index.html took it out before any
    // analytics tag could record it. Wherever this hook drops the saved token, it forgets that copy too.
    const joinTokenParam = readUrlSecret('join');
    if (joinTokenParam) {
      setJoinToken(joinTokenParam);
      writeStored('pendingJoinToken', JSON.stringify({ token: joinTokenParam, expires: Date.now() + 7 * 24 * 60 * 60 * 1000 }));
      updateIsPostInstall(false);
    } else {
      const savedJoinTokenRaw = readStored('pendingJoinToken');
      if (savedJoinTokenRaw) {
        try {
          const parsed = JSON.parse(savedJoinTokenRaw);
          if (parsed.token && parsed.expires > Date.now()) {
            setJoinToken(parsed.token);
            // Don't clear isPostInstall for restored join tokens — only a fresh
            // ?join param in the URL should override the post-install flow.
          } else {
            removeStored('pendingJoinToken');
          }
        } catch {
          removeStored('pendingJoinToken');
        }
      }
    }
  }, []);

  /**
   * ENG-3406: at sign-out, drop the decision a signed-in person was asked to make. The "join this
   * organization?" dialog is App state, so left open it came back for the next person to sign in on
   * this tab, holding the previous person's invitation.
   *
   * The invitation itself (`joinToken`, `inviteContext`) is left alone, as App's sign-out already left
   * `inviteContext`, so the sign-in screen keeps showing a link that was opened for someone signed out.
   * Its saved copy (`pendingJoinToken`) is cleared by lib/userSession.
   */
  const resetForSignOut = useCallback(() => {
    setJoinConfirmDialogOpen(false);
    setPendingJoinToken(null);
  }, []);

  return useMemo(() => ({
    joinToken,
    setJoinToken,
    joinResult,
    setJoinResult,
    pendingJoinToken,
    joinConfirmDialogOpen,
    inviteContext,
    handleConfirmJoinBusiness,
    handleCancelJoinBusiness,
    initJoinToken,
    resetForSignOut,
  }), [
    joinToken,
    joinResult,
    pendingJoinToken,
    joinConfirmDialogOpen,
    inviteContext,
    handleConfirmJoinBusiness,
    handleCancelJoinBusiness,
    initJoinToken,
    resetForSignOut,
  ]);
}
